import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid, Stack, Typography } from '@mui/material';
import {
  ParkingLot,
  ParkingLotName,
  ParkingLotSectionDriveIn,
  ParkingLotSectionSeasonsPrices,
  ParkingLotSectionWithReservation,
  ParkingPrice,
} from '../../../../domain/parking';
import { ParkingLotCard } from '../parking-lot-card';
import { LotsContainer } from './parking-lots-select.styles';
import { Loader } from '../../../../components/Loader';

import { LotsMap } from '../maps/';
import { PromotionDisclaimer } from '../../../bundles/components/promotion-disclaimer';
import useBundleRecommendationContext from '../../../../context/BundleRecommendation/use-bundle-recommendation-context';
import { useI18next } from 'gatsby-plugin-react-i18next';

interface Props {
  sections: {
    section: ParkingLotSectionWithReservation | ParkingLotSectionDriveIn;
    seasonsPrices: ParkingLotSectionSeasonsPrices;
    lots: ParkingLot[];
  }[];
  selectedLotName?: ParkingLotName;
  parkingPrice: ParkingPrice | null;
  numbersOfVehicles: number;
  onSelect: (lot: ParkingLot) => void;
}

export const ParkingLotSelect = ({ sections, selectedLotName, parkingPrice, numbersOfVehicles, onSelect }: Props) => {
  const { t } = useTranslation();

  const [parkingLotSection, setParkingLotSection] = useState<
    ParkingLotSectionWithReservation | ParkingLotSectionDriveIn | null
  >(null);

  const { parkingMessages } = useBundleRecommendationContext();
  const { language: locale } = useI18next();

  const handleSelectLot = (section: ParkingLotSectionWithReservation | ParkingLotSectionDriveIn, lot: ParkingLot) => {
    setParkingLotSection(section);
    onSelect(lot);
  };

  const getTotal = (section: ParkingLotSectionWithReservation | ParkingLotSectionDriveIn) => {
    if (parkingLotSection !== section) return null;
    if (!parkingPrice) return null;

    return parkingPrice.price * numbersOfVehicles;
  };

  return (
    <Grid container alignItems="center" spacing={3} mb={4}>
      <Grid item xs={12} md={6}>
        <Stack sx={{ padding: { xs: 0, md: 8 } }} alignItems="center" justifyContent="center">
          <LotsMap selectedLot={selectedLotName} selectLot={handleSelectLot} sections={sections} />
        </Stack>
      </Grid>
      <Grid item xs={12} md={6}>
        <LotsContainer>
          {sections ? (
            sections.map((section) => (
              <ParkingLotCard
                key={section.section}
                total={getTotal(section.section)}
                selectedLotName={selectedLotName}
                {...section}
                onSelect={(lot) => handleSelectLot(section.section, lot)}
              />
            ))
          ) : (
            <Loader />
          )}
        </LotsContainer>

        <Typography color="secondary.light" fontSize={'0.8rem'} fontWeight={700} mt={4}>
          {t('parking_service.parking_lots_unpaved')}
        </Typography>
        {parkingMessages?.loteMessage && <PromotionDisclaimer product='parking'>{parkingMessages.loteMessage[locale]}</PromotionDisclaimer>}
      </Grid>
    </Grid>
  );
};
